import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchScores, etDay, formatGameTime } from '../../lib/scoresApi';

const TEAM_ORDER = ['commanders', 'nationals', 'capitals', 'wizards', 'mystics', 'dcunited'];

const featuredGame = (games) => {
  const sorted = [...games].sort((a, b) => new Date(a.date) - new Date(b.date));
  const live = sorted.find((g) => g.status === 'LIVE');
  if (live) return live;
  const today = etDay(new Date().toISOString());
  const todayGame = sorted.find((g) => etDay(g.date) === today);
  if (todayGame) return todayGame;
  const upcoming = sorted.find(
    (g) => ['UPCOMING', 'DELAYED', 'POSTPONED'].includes(g.status) && new Date(g.date) > new Date()
  );
  if (upcoming) return upcoming;
  const finals = sorted.filter((g) => g.status === 'FINAL');
  return finals[finals.length - 1] || null;
};

const StatusLabel = ({ game }) => {
  if (game.status === 'LIVE') {
    return (
      <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#BF0A30]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#BF0A30] animate-pulse" />
        Live{game.status_detail ? ` · ${game.status_detail}` : ''}
      </span>
    );
  }
  if (game.status === 'FINAL') {
    return <span className="text-[10px] font-bold uppercase tracking-wide text-slate-600">Final</span>;
  }
  if (game.status === 'POSTPONED' || game.status === 'DELAYED') {
    return <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700">{game.status}</span>;
  }
  return <span className="text-[10px] font-medium text-slate-500">{formatGameTime(game.date)}</span>;
};

const TeamRow = ({ side, opponentScore, isFinal }) => {
  const score = side.score;
  const won = isFinal && score != null && opponentScore != null && Number(score) > Number(opponentScore);
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 min-w-0">
        {side.logo && (
          <img src={side.logo} alt={side.name || side.abbr} width="16" height="16" className="w-4 h-4 object-contain shrink-0" loading="lazy" />
        )}
        <span className={`text-xs truncate ${won || !isFinal ? 'font-bold text-slate-900' : 'font-medium text-slate-500'}`}>
          {side.abbr}
        </span>
        {side.record && <span className="text-[10px] text-slate-400 shrink-0">{side.record}</span>}
      </div>
      {score != null && (
        <span className={`text-sm tabular-nums ${won || !isFinal ? 'font-bold text-slate-900' : 'font-medium text-slate-500'}`}>
          {score}
        </span>
      )}
    </div>
  );
};

const ScoreBar = () => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setData(await fetchScores());
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const live = data && (data.games || []).some((g) => g.status === 'LIVE');
    const timer = setInterval(load, live ? 30000 : 300000);
    return () => clearInterval(timer);
  }, [data, load]);

  if (!data) return null;

  const cards = TEAM_ORDER.map((key) => ({
    key,
    game: featuredGame((data.games || []).filter((g) => g.team_key === key)),
  })).filter((c) => c.game);

  return (
    <div data-testid="score-bar" className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto flex items-stretch">
        <Link
          to="/scores"
          data-testid="score-bar-label"
          className="hidden sm:flex flex-col justify-center px-4 border-r border-slate-200 shrink-0 hover:bg-slate-50 transition-colors"
        >
          <span className="font-heading text-xs text-[#002868] uppercase tracking-[0.2em]">DC</span>
          <span className="font-heading text-xs text-[#BF0A30] uppercase tracking-[0.2em]">Scores</span>
        </Link>

        {!data.available && cards.length === 0 ? (
          <div data-testid="score-bar-unavailable" className="flex items-center px-4 py-3 text-xs text-slate-500">
            Scores temporarily unavailable
          </div>
        ) : (
          <div className="flex overflow-x-auto divide-x divide-slate-200 flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {cards.map(({ key, game }) => {
              const isFinal = game.status === 'FINAL';
              return (
                <Link
                  key={key}
                  to="/scores"
                  data-testid={`score-bar-card-${key}`}
                  className="flex flex-col justify-center gap-0.5 px-3 py-1.5 min-w-[150px] shrink-0 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{game.league}</span>
                    <StatusLabel game={game} />
                  </div>
                  <TeamRow side={game.away} opponentScore={game.home.score} isFinal={isFinal} />
                  <TeamRow side={game.home} opponentScore={game.away.score} isFinal={isFinal} />
                </Link>
              );
            })}
          </div>
        )}
      </div>
      {data.stale && data.updated_at && (
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-[10px] text-slate-400 py-0.5">
            Showing last available scores · updated{' '}
            {new Date(data.updated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </p>
        </div>
      )}
    </div>
  );
};

export default ScoreBar;
